import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Stack
} from "@mui/material";
import { createStripeCheckoutSession } from "../network/stripe_api";
import { User } from "../models/user";
import ConfirmUpgradePopup from "../components/ConfirmUpgradePopup";

interface PricingPageProps {
  user: User | null;
}

export type TierId = "free" | "pro" | "premium";

interface Tier {
  tierId: TierId;
  name: string;
  price: number;
  description: string;
  features: string[];
  color: string;
}

const tiers: Tier[] = [
  {
    tierId: "free",
    name: "Basic",
    price: 0,
    description: "Free tier to get you started",
    features: [
      "Up to 2 route generations per day",
      "1 route download per day",
      "Save 1 route",
      "Basic route access",
    ],
    color: "#333",
  },
  {
    tierId: "pro",
    name: "Pro",
    price: 2.99,
    description: "For regular users who want more",
    features: [
      "Up to 25 route generations per day",
      "12 route downloads per day",
      "Save up to 10 routes",
      "All Basic features",
    ],
    color: "#1976d2", // MUI primary
  },
  {
    tierId: "premium",
    name: "Premium",
    price: 5.99,
    description: "Full access for power users",
    features: [
      "Up to 50 route generations per day",
      "25 route downloads per day",
      "Save up to 25 routes",
      "All Pro features",
    ],
    color: "#FFD700", // Gold
  },
];


const PricingPage: React.FC<PricingPageProps> = ({ user }) => {
  const [confirmPopupOpen, setConfirmPopupOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<TierId | null>(null);
  // const handleSubscribe = async (tierId: TierId) => {
  //   try {
  //     const data = await createStripeCheckoutSession(tierId);

  //     if (data.url) {
  //       // Redirect to Stripe Checkout page
  //       window.location.href = data.url;
  //     } else if (data.message) {
  //       // Show confirmation message (subscription updated)
  //       alert(data.message);
  //       // Optionally refresh user info here
  //     } else {
  //       alert("Unexpected response from server.");
  //     }
  //   } catch (err) {
  //     alert(`Error: ${(err as Error).message}`);
  //   }

  // };
  const handleSubscribe = async () => {
    if (!selectedTier) return;

    try {
      const data = await createStripeCheckoutSession(selectedTier);

      if (data.url) {
        window.location.href = data.url;
      } else if (data.message) {
        alert(data.message);
      } else {
        alert("Unexpected response from server.");
      }
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    }
    setConfirmPopupOpen(false);
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: "auto", mt: 8, px: 3, backgroundColor:'white', p:4, borderRadius:8}}>
      <Typography variant="h3" align="center" fontWeight={700} gutterBottom>
        {user ? "Looking To Switch?" : "Our Pricing"}
      </Typography>
      <Typography variant="subtitle1" align="center" color="text.secondary" mb={4}>
        {
        user ? "Switching is as easy as one click. Upgrade or downgrade anytime with no hassle."
        :'Simple pricing. No hidden fees. Cancel anytime.'}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
          gap: 4,
        }}
      >
        {tiers.map((tier) => {
          const isCurrent = user?.subscriptionStatus === tier.tierId;

          return (
            <Card
              key={tier.tierId}
              elevation={4}
              sx={{
                borderRadius: 3,
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                },
              }}
            >
            <ConfirmUpgradePopup
              tierId={selectedTier}
              isOpen={confirmPopupOpen}
              onClose={() => setConfirmPopupOpen(false)}
              onConfirm={handleSubscribe}
            />

              <CardContent>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {tier.name}
                </Typography>

                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{
                    color: tier.tierId === "premium" ? "#c89c00" : tier.color,
                    mb: 1,
                  }}
                >
                  {tier.price === 0 ? "Free" : `£${tier.price}/mo`}
                </Typography>

                <Typography variant="body1" color="text.secondary" mb={2}>
                  {tier.description}
                </Typography>

                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                  {tier.features.map((feature, i) => (
                    <Typography
                      component="li"
                      key={i}
                      variant="body2"
                      color="text.secondary"
                    >
                      {feature}
                    </Typography>
                  ))}
                </Box>
              </CardContent>

              {user && (
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button
                    fullWidth
                    variant={isCurrent ? "outlined" : "contained"}
                    onClick={() => {
                      if (!isCurrent) {
                        setSelectedTier(tier.tierId); // store tier
                        setConfirmPopupOpen(true);    // open popup
                      }
                    }}
                    disabled={isCurrent}
                    sx={{
                      backgroundColor: isCurrent ? "#f5f5f5" : tier.color,
                      color: isCurrent ? "text.primary" : "#fff",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: isCurrent ? "#e0e0e0" : undefined,
                      },
                    }}
                  >
                    {isCurrent ? "Current Plan" : `Switch to ${tier.name}`}
                  </Button>
                </CardActions>
              )}
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default PricingPage;
